'use client';

'use client';

import React from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { redirect } from 'next/navigation';
import LoginPopup from '@/app/popup/login-popup';

// Import our organized components and hooks
import { ChatLayout, UnifiedChatPanel } from './components/ChatLayout';
import { Loading, Error, MobileChatLoading } from './components/LoadingStates';
import { StreamingPanel } from './components/StreamingPanel';
import { useNavbar } from '@/app/lib/contexts/NavbarContext';

import { useGlobalChatState } from '../../hooks/useGlobalChatState';
import { LiveImageStatus } from '../../data/liveMessageData/liveMessage';
import { ChatStatus } from '../../data/globalChatState';

export default function MobileChatPage({
  params,
  initiateVideoRoom,
}: {
  params: Promise<{ avatarId: string }>;
  initiateVideoRoom: () => Promise<void>;
}) {
  // Use global chat state for character data and state management
  const [globalState, globalActions] = useGlobalChatState();
  const { 
    character: character, 
    isCharacterLoading: isLoading, 
    characterLoadingError: loadingError,
    activeImage: activeBackgroundImage,
    profileImage: profileImage,
    chatStatus: chatStatus 
  } = globalState;

  // Navbar hiding functionality
  const { setIsNavbarHidden } = useNavbar();

  // Hide navbar when component mounts and restore when unmounts
  React.useEffect(() => {
    setIsNavbarHidden(true);
    
    // Cleanup: restore navbar when component unmounts
    return () => {
      setIsNavbarHidden(false);
    };
  }, [setIsNavbarHidden]);

  // Handle not-found case in useEffect to avoid hook ordering issues
  React.useEffect(() => {
    if (loadingError === 'NOT_FOUND') {
      redirect('/not-found'); // Redirect to the new not-found page with navbar
    }
  }, [loadingError]);

  // Auto-collapse sidebar when entering chat
  React.useEffect(() => {
    const collapseNavbar = () => {
      // Find the navbar collapse button and click it
      const navbar = document.querySelector('nav');
      if (navbar) {
        // Check if navbar is not already collapsed
        const isCurrentlyCollapsed = navbar.offsetWidth <= 80;
        if (!isCurrentlyCollapsed) {
          // Find the collapse button (the MenuIcon button when expanded)
          const collapseButton = navbar.querySelector('button[class*="w-8 h-8"]');
          if (collapseButton) {
            (collapseButton as HTMLButtonElement).click();
          }
        }
      }
    };

    // Delay to ensure DOM is ready
    const timer = setTimeout(collapseNavbar, 100);
    
    return () => clearTimeout(timer);
  }, []); // Only run once when component mounts

  // Show UI immediately when avatar data is available
  if (isLoading) {
    return <MobileChatLoading/>;
  }

  // Handle not-found case specifically (redirect is called in useEffect above)
  if (loadingError === 'NOT_FOUND') {
    return <Error error="Character not found" />;
  }

  // Other error states  
  if (loadingError) {
    return <Error error={loadingError} />;
  }

  // Avatar should be available here since not-found case is handled above
  if (!character) {
    return <MobileChatLoading />;
  }

  // Show UI immediately - chat history is already loaded with character data
  // Video mode - New layout design
  if (chatStatus !== ChatStatus.INFO) {
    return (
      <div className="bg-[#121214] w-full h-full">
            <ChatLayout>
          <StreamingPanel
            activeBackgroundImage={activeBackgroundImage}
          />
          {/* Chat Section - Overlay layer positioned relative to ChatLayout */}
          <div className="absolute inset-0 z-10 pointer-events-none">
            <div className="h-full pointer-events-auto">
              <UnifiedChatPanel
                initiateVideoRoom={initiateVideoRoom}
              />
            </div>
          </div>
        </ChatLayout>
        
      </div>
    );
  }

  // Regular chat mode (non-video) - Using Unified Component
  return (
    <div className="bg-[#121214] w-full h-full">
        <ChatLayout>
        <StreamingPanel
          activeBackgroundImage={activeBackgroundImage}
        />
        
        {/* Chat Section - Overlay layer positioned relative to ChatLayout */}
        <div className="absolute inset-0 z-10 pointer-events-none">
          <div className="h-full pointer-events-auto">
            <UnifiedChatPanel
              initiateVideoRoom={initiateVideoRoom}
            />
          </div>
        </div>
      </ChatLayout>
      
    </div>
  );
} 